// PDFViewerWithInput.js
import React, { useState, useEffect, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Configure PDF.js worker with fallbacks for different environments
if (typeof window !== 'undefined') {
    // Method 1: Try CDN first (works everywhere)
    const cdnWorker = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;
    
    // Method 2: Try unpkg CDN as fallback
    const unpkgWorker = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;
    
    // Method 3: Try local file (for development)
    let localWorker;
    try {
        localWorker = new URL(
            'pdfjs-dist/build/pdf.worker.min.js',
            import.meta.url,
        ).toString();
    } catch (e) {
        console.log('Local worker URL creation failed:', e);
    }
    
    // Set worker source with priority order
    pdfjs.GlobalWorkerOptions.workerSrc = cdnWorker;
    
    // Log which worker is being used
    console.log('PDF.js Worker configured:', pdfjs.GlobalWorkerOptions.workerSrc);
    console.log('PDF.js version:', pdfjs.version);
}

const PDFViewerWithInput = () => {
    const [inputReportId, setInputReportId] = useState('');
    const [reportId, setReportId] = useState('');
    const [pdfUrl, setPdfUrl] = useState(null);
    const [pdfBlob, setPdfBlob] = useState(null);
    const [numPages, setNumPages] = useState(null);
    const [pageNumber, setPageNumber] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showViewer, setShowViewer] = useState(false);
    const [pdfScale, setPdfScale] = useState(1.0);
    const containerRef = useRef(null);
    const [workerRetryCount, setWorkerRetryCount] = useState(0);

    // Verify worker is accessible
    useEffect(() => {
        const checkWorker = async () => {
            try {
                const workerUrl = pdfjs.GlobalWorkerOptions.workerSrc;
                console.log('Checking worker at:', workerUrl);
                
                const response = await fetch(workerUrl, { method: 'HEAD' });
                if (!response.ok && workerRetryCount < 2) {
                    console.warn(`Worker not accessible (${response.status}), trying fallback...`);
                    
                    // Try fallback workers
                    if (workerRetryCount === 0) {
                        pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;
                    } else if (workerRetryCount === 1) {
                        pdfjs.GlobalWorkerOptions.workerSrc = `//cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;
                    }
                    
                    setWorkerRetryCount(prev => prev + 1);
                    console.log('Trying fallback worker:', pdfjs.GlobalWorkerOptions.workerSrc);
                } else if (response.ok) {
                    console.log('Worker accessible:', workerUrl);
                }
            } catch (err) {
                console.error('Worker check failed:', err);
            }
        };
        
        checkWorker();
    }, [workerRetryCount]);

    // Cleanup blob URL on unmount
    useEffect(() => {
        return () => {
            if (pdfUrl) {
                URL.revokeObjectURL(pdfUrl);
            }
        };
    }, [pdfUrl]);

    const fetchPDF = async (id) => {
        if (!id) {
            setError('Please enter a Report ID');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await fetch(
                `https://apicc.blueskytechmedia.in/api/easy/download-report/${id}`,
                {
                    method: 'GET',
                    headers: {
                        'Accept': 'application/pdf',
                    },
                    mode: 'cors'
                }
            );

            if (!response.ok) {
                throw new Error(`Server returned ${response.status}: ${response.statusText}`);
            }

            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/pdf')) {
                console.warn('Unexpected content type:', contentType);
                throw new Error(`Expected PDF but received ${contentType || 'unknown content type'}`);
            }

            const blob = await response.blob();
            console.log('PDF blob size:', blob.size, 'bytes');

            if (blob.size < 1000) {
                throw new Error(`PDF file too small (${blob.size} bytes) - invalid or empty`);
            }

            const url = URL.createObjectURL(blob);
            console.log('Created blob URL:', url);
            
            setPdfUrl(url);
            setPdfBlob(blob);
            setReportId(id);
            setShowViewer(true);
            setPageNumber(1);
            setError(null);

        } catch (err) {
            console.error('PDF fetch error:', err);
            setError(`Failed to load PDF: ${err.message}`);
            setShowViewer(false);
            if (pdfUrl) {
                URL.revokeObjectURL(pdfUrl);
                setPdfUrl(null);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (pdfUrl) {
            URL.revokeObjectURL(pdfUrl);
            setPdfUrl(null);
        }
        fetchPDF(inputReportId.trim());
    };

    const onDocumentLoadSuccess = ({ numPages }) => {
        console.log('PDF loaded successfully, pages:', numPages);
        setNumPages(numPages);
        setError(null);
    };

    const onDocumentLoadError = (error) => {
        console.error('PDF load error:', error);
        
        let errorMessage = 'Failed to render PDF. ';
        
        if (error.message && error.message.includes('worker')) {
            errorMessage += 'Worker configuration issue. Please check your internet connection or contact support.';
        } else if (error.message) {
            errorMessage += error.message;
        } else {
            errorMessage += 'Unknown error occurred.';
        }
        
        setError(errorMessage);
        setShowViewer(false);
    };

    const goToPrevPage = () => setPageNumber(p => Math.max(p - 1, 1));
    const goToNextPage = () => setPageNumber(p => Math.min(p + 1, numPages));

    const closeViewer = () => {
        setShowViewer(false);
        if (pdfUrl) {
            URL.revokeObjectURL(pdfUrl);
            setPdfUrl(null);
        }
        setNumPages(null);
        setPageNumber(1);
        setError(null);
        setInputReportId('');
    };

    const zoomIn = () => setPdfScale(s => Math.min(s + 0.2, 2.5));
    const zoomOut = () => setPdfScale(s => Math.max(s - 0.2, 0.5));
    
    const downloadPDF = () => {
        if (!pdfBlob) {
            setError('No PDF available to download');
            return;
        }

        try {
            const downloadUrl = URL.createObjectURL(pdfBlob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = `report_${reportId}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            setTimeout(() => {
                URL.revokeObjectURL(downloadUrl);
            }, 100);
            
            console.log('PDF download started');
        } catch (err) {
            console.error('Download error:', err);
            setError(`Failed to download PDF: ${err.message}`);
        }
    };

    return (
        <div style={styles.container}>
            <h2 style={styles.title}>PDF Report Viewer</h2>

            <form onSubmit={handleSubmit} style={styles.form}>
                <div style={styles.inputGroup}>
                    <label style={styles.label}>Enter Report ID:</label>
                    <input
                        type="text"
                        value={inputReportId}
                        onChange={e => setInputReportId(e.target.value)}
                        placeholder="Enter Report ID"
                        style={styles.input}
                        disabled={loading}
                    />
                    <button 
                        type="submit" 
                        style={styles.button} 
                        disabled={loading}
                    >
                        {loading ? 'Loading...' : 'View Report'}
                    </button>
                </div>
            </form>

            {error && (
                <div style={styles.error}>
                    ❌ {error}
                    {error.includes('worker') && (
                        <div style={styles.hint}>
                            <strong>Worker Configuration Issue:</strong> The PDF viewer is having trouble loading. 
                            Please try refreshing the page or check your internet connection.
                            {process.env.NODE_ENV === 'development' && (
                                <div style={{ marginTop: 10, fontSize: 11 }}>
                                    <strong>Debug Info:</strong><br/>
                                    Worker URL: {pdfjs.GlobalWorkerOptions.workerSrc}<br/>
                                    PDF.js Version: {pdfjs.version}
                                </div>
                            )}
                        </div>
                    )}
                    {error.includes('CORS') && (
                        <div style={styles.hint}>
                            <strong>Note:</strong> If this is a CORS issue, the API server needs to include appropriate CORS headers.
                            You might need to use a proxy or contact the API provider.
                        </div>
                    )}
                </div>
            )}

            {showViewer && pdfUrl && (
                <div style={styles.viewerContainer} ref={containerRef}>
                    <div style={styles.viewerHeader}>
                        <div>
                            <h3>Report ID: {reportId}</h3>
                            {numPages && (
                                <div style={styles.pageInfo}>
                                    Page {pageNumber} of {numPages}
                                </div>
                            )}
                        </div>
                        <div style={styles.controls}>
                            <button 
                                onClick={downloadPDF} 
                                style={styles.downloadBtn}
                                title="Download PDF"
                            >
                                💾 Download
                            </button>
                            <button onClick={zoomOut} style={styles.controlBtn} title="Zoom Out">🔍-</button>
                            <span style={styles.zoomLevel}>{Math.round(pdfScale * 100)}%</span>
                            <button onClick={zoomIn} style={styles.controlBtn} title="Zoom In">🔍+</button>
                            <button onClick={goToPrevPage} disabled={pageNumber <= 1} style={styles.controlBtn}>
                                ← Prev
                            </button>
                            <button onClick={goToNextPage} disabled={pageNumber >= numPages} style={styles.controlBtn}>
                                Next →
                            </button>
                            <button onClick={closeViewer} style={styles.closeBtn}>
                                ✖ Close
                            </button>
                        </div>
                    </div>

                    <div style={styles.pdfContent}>
                        <Document
                            file={pdfUrl}
                            onLoadSuccess={onDocumentLoadSuccess}
                            onLoadError={onDocumentLoadError}
                            loading={<div style={styles.loading}>Loading PDF document...</div>}
                            error={<div style={styles.errorMsg}>Failed to load PDF document. Please check if the file is valid.</div>}
                        >
                            <Page
                                pageNumber={pageNumber}
                                scale={pdfScale}
                                renderTextLayer={false}
                                renderAnnotationLayer={false}
                                renderInteractiveForms={false}
                                width={undefined}
                                onRenderSuccess={() => console.log(`Page ${pageNumber} rendered successfully`)}
                                onRenderError={(error) => console.error('Page render error:', error)}
                            />
                        </Document>
                    </div>
                </div>
            )}

            {loading && !showViewer && (
                <div style={styles.loadingContainer}>
                    <div style={styles.loadingSpinner}></div>
                    <p>Downloading PDF...</p>
                </div>
            )}
        </div>
    );
};

const styles = {
    container: { 
        maxWidth: '1200px', 
        margin: '0 auto', 
        padding: 20, 
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif' 
    },
    title: { 
        textAlign: 'center', 
        color: '#333',
        marginBottom: 30 
    },
    form: { 
        marginBottom: 20,
        backgroundColor: '#f8f9fa',
        padding: 20,
        borderRadius: 8,
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    },
    inputGroup: { 
        display: 'flex', 
        gap: 10, 
        alignItems: 'center',
        flexWrap: 'wrap'
    },
    label: { 
        fontWeight: 'bold',
        color: '#555'
    },
    input: { 
        padding: '10px 12px', 
        fontSize: 16, 
        flex: 1,
        border: '1px solid #ddd',
        borderRadius: 4,
        transition: 'border-color 0.2s',
        outline: 'none'
    },
    button: { 
        padding: '10px 24px', 
        background: '#007bff', 
        color: '#fff', 
        border: 'none',
        borderRadius: 4,
        cursor: 'pointer',
        fontWeight: 'bold',
        transition: 'background 0.2s',
        '&:hover': {
            background: '#0056b3'
        },
        '&:disabled': {
            background: '#ccc',
            cursor: 'not-allowed'
        }
    },
    error: { 
        background: '#f8d7da', 
        padding: 15, 
        marginTop: 10, 
        color: '#721c24',
        borderRadius: 4,
        border: '1px solid #f5c6cb'
    },
    hint: {
        marginTop: 10,
        fontSize: 12,
        color: '#666',
        background: '#fff',
        padding: 8,
        borderRadius: 4
    },
    viewerContainer: { 
        marginTop: 20, 
        border: '1px solid #ddd',
        borderRadius: 8,
        overflow: 'hidden',
        backgroundColor: '#fff'
    },
    viewerHeader: { 
        padding: '15px 20px', 
        background: '#f8f9fa',
        borderBottom: '1px solid #ddd',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 10
    },
    pageInfo: {
        fontSize: 14,
        color: '#666',
        marginTop: 5
    },
    controls: { 
        display: 'flex', 
        gap: 8, 
        alignItems: 'center',
        flexWrap: 'wrap'
    },
    controlBtn: {
        padding: '6px 12px',
        background: '#fff',
        border: '1px solid #ddd',
        borderRadius: 4,
        cursor: 'pointer',
        fontSize: 14,
        transition: 'all 0.2s',
        '&:hover': {
            background: '#e9ecef'
        },
        '&:disabled': {
            opacity: 0.5,
            cursor: 'not-allowed'
        }
    },
    downloadBtn: {
        padding: '6px 12px',
        background: '#28a745',
        color: '#fff',
        border: 'none',
        borderRadius: 4,
        cursor: 'pointer',
        fontSize: 14,
        fontWeight: 'bold',
        transition: 'background 0.2s',
        '&:hover': {
            background: '#218838'
        }
    },
    closeBtn: {
        padding: '6px 12px',
        background: '#dc3545',
        color: '#fff',
        border: 'none',
        borderRadius: 4,
        cursor: 'pointer',
        fontSize: 14,
        transition: 'background 0.2s',
        '&:hover': {
            background: '#c82333'
        }
    },
    zoomLevel: {
        fontSize: 12,
        minWidth: 45,
        textAlign: 'center'
    },
    pdfContent: { 
        padding: 20, 
        display: 'flex', 
        justifyContent: 'center', 
        background: '#e9ecef',
        overflowX: 'auto',
        minHeight: 500
    },
    loading: {
        padding: 40,
        textAlign: 'center',
        color: '#666'
    },
    errorMsg: {
        padding: 40,
        textAlign: 'center',
        color: '#dc3545'
    },
    loadingContainer: {
        textAlign: 'center',
        padding: 40,
        backgroundColor: '#f8f9fa',
        borderRadius: 8,
        marginTop: 20
    },
    loadingSpinner: {
        border: '3px solid #f3f3f3',
        borderTop: '3px solid #007bff',
        borderRadius: '50%',
        width: 40,
        height: 40,
        animation: 'spin 1s linear infinite',
        margin: '0 auto 20px'
    }
};

// Add CSS animation
const styleSheet = document.createElement("style");
styleSheet.textContent = `
    @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    }
`;
document.head.appendChild(styleSheet);

export default PDFViewerWithInput;
